import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUpTrayIcon,
  ChevronDownIcon,
  XMarkIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { uploadDocumentFiles } from "../components/dashboard/Media/document-upload-service";
import { TWclassNames } from "../components/utils/Div";

/** Above PMDialog (2000–3500), MUI overlays (3200), and other app modals (up to ~9999). */
export const DOCUMENT_UPLOAD_BAR_Z_INDEX = 10100;

const DocumentUploadQueueContext = createContext(null);

function createSessionId() {
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function getDocumentUploadDestinationLabel(module, moduleParam, subtitle, t) {
  if (subtitle) {
    return subtitle;
  }
  const key = `documentUpload.destination.${module}`;
  const moduleLabel = t(key, { defaultValue: module });
  if (moduleParam != null && moduleParam !== "") {
    return `${moduleLabel} (#${moduleParam})`;
  }
  return moduleLabel;
}

function aggregateProgress(sessions) {
  if (!sessions.length) return 0;
  const sum = sessions.reduce((acc, s) => acc + (s.overallProgress || 0), 0);
  return Math.round(sum / sessions.length);
}

export function DocumentUploadQueueProvider({ children }) {
  const [sessions, setSessions] = useState([]);
  const [barExpanded, setBarExpanded] = useState(true);
  const callbacksRef = useRef(new Map());
  const processingRef = useRef(new Set());
  const filesRef = useRef(new Map());

  useEffect(() => {
    if (!sessions.some((s) => ["uploading", "queued", "failed"].includes(s.status))) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [sessions]);

  const updateSession = useCallback((sessionId, patch) => {
    setSessions((current) =>
      current.map((s) => (s.id === sessionId ? { ...s, ...patch } : s))
    );
  }, []);

  const removeSessionFromUi = useCallback((sessionId, delayMs = 4000) => {
    setTimeout(() => {
      setSessions((current) => current.filter((s) => s.id !== sessionId));
      callbacksRef.current.delete(sessionId);
      processingRef.current.delete(sessionId);
      filesRef.current.get(sessionId)?.forEach((entry) => { if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl); });
      filesRef.current.delete(sessionId);
    }, delayMs);
  }, []);

  const closeSessionNotification = useCallback((sessionId) => {
    setSessions((current) => current.filter((s) => s.id !== sessionId));
    callbacksRef.current.delete(sessionId);
    filesRef.current.get(sessionId)?.forEach((entry) => { if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl); });
    filesRef.current.delete(sessionId);
  }, []);

  const runSession = useCallback(
    async (sessionId, files, destinationLabel, module, moduleParam) => {
      if (processingRef.current.has(sessionId)) {
        return;
      }
      processingRef.current.add(sessionId);

      const callbacks = callbacksRef.current.get(sessionId) || {};
      const totalCount = files.length;
      let uploadedCount = 0;
      const fileProgress = files.map((f) => ({
        name: f.file?.name || f.title || "",
        title: f.title || f.file?.name || "",
        progress: 0,
      }));

      updateSession(sessionId, {
        status: "uploading",
        destinationLabel,
        files: fileProgress,
        uploadedCount: 0,
        totalCount,
        overallProgress: 0,
      });

      try {
        const uploadedDocuments = await uploadDocumentFiles(files, {
          onProgressChange: (progressMap) => {
            const values = Object.values(progressMap);
            const latest = values.length ? values[values.length - 1] : 0;
            const overall =
              totalCount > 0
                ? Math.round(
                    ((uploadedCount * 100 + latest) / (totalCount * 100)) * 100
                  )
                : 0;

            setSessions((current) =>
              current.map((s) => {
                if (s.id !== sessionId) return s;
                const nextFiles = [...(s.files || fileProgress)];
                const idx = Math.min(uploadedCount, nextFiles.length - 1);
                if (idx >= 0 && nextFiles[idx]) {
                  nextFiles[idx] = { ...nextFiles[idx], progress: latest };
                }
                return {
                  ...s,
                  files: nextFiles,
                  overallProgress: Math.min(100, overall),
                };
              })
            );

            if (callbacks.onProgress) {
              callbacks.onProgress(progressMap);
            }
          },
          onFileUploadComplete: () => {
            uploadedCount += 1;
            setSessions((current) =>
              current.map((s) => {
                if (s.id !== sessionId) return s;
                const nextFiles = [...(s.files || fileProgress)];
                const idx = uploadedCount - 1;
                if (idx >= 0 && nextFiles[idx]) {
                  nextFiles[idx] = { ...nextFiles[idx], progress: 100 };
                }
                return {
                  ...s,
                  uploadedCount,
                  files: nextFiles,
                  overallProgress: Math.round((uploadedCount / totalCount) * 100),
                };
              })
            );
            if (callbacks.onFileComplete) {
              callbacks.onFileComplete();
            }
          },
        });

        updateSession(sessionId, {
          status: "completed",
          overallProgress: 100,
          uploadedCount: totalCount,
        });

        if (callbacks.onComplete) {
          try { callbacks.onComplete(uploadedDocuments); } catch (_) { /* Upload is confirmed; a stale consumer cannot undo it. */ }
        }

        window.dispatchEvent(
          new CustomEvent("promang:documents-uploaded", {
            detail: {
              module,
              moduleParam,
              documents: uploadedDocuments,
            },
          })
        );

        callbacksRef.current.delete(sessionId);
        removeSessionFromUi(sessionId, 5000);
      } catch (error) {
        console.error("Background document upload failed:", error);
        updateSession(sessionId, {
          status: "failed",
          error: error?.message || "Upload failed",
        });

        if (callbacks.onError) {
          callbacks.onError(error);
        }

        // Keep failed uploads and their bytes available for an explicit retry.
      } finally {
        processingRef.current.delete(sessionId);
      }
    },
    [removeSessionFromUi, updateSession]
  );

  const enqueueUpload = useCallback(
    ({
      files,
      destinationLabel,
      module,
      moduleParam,
      onProgress,
      onFileComplete,
      onComplete,
      onError,
    }) => {
      if (!files?.length) {
        return null;
      }

      const sessionId = createSessionId();
      const retryableFiles = files.map((entry) => ({ ...entry, uploadCheckpoint: entry.uploadCheckpoint || {} }));
      filesRef.current.set(sessionId, retryableFiles);
      const label =
        destinationLabel ||
        (module && moduleParam != null
          ? `${module} (#${moduleParam})`
          : module || "");

      setBarExpanded(true);

      setSessions((current) => [
        ...current,
        {
          id: sessionId,
          destinationLabel: label,
          module,
          moduleParam,
          previews: files.map((f) => f.previewUrl).filter(Boolean),
          previewItems: files.filter((f) => f.previewUrl).map((f) => ({ url: f.previewUrl, type: f.file?.type, title: f.title || f.file?.name })),
          status: "queued",
          files: files.map((f) => ({
            name: f.file?.name || "",
            title: f.title || f.file?.name || "",
            progress: 0,
          })),
          uploadedCount: 0,
          totalCount: files.length,
          overallProgress: 0,
        },
      ]);

      callbacksRef.current.set(sessionId, {
        module,
        moduleParam,
        onProgress,
        onFileComplete,
        onComplete,
        onError,
      });

      void runSession(sessionId, retryableFiles, label, module, moduleParam);
      return sessionId;
    },
    [runSession]
  );

  const minimizeBar = useCallback(() => setBarExpanded(false), []);
  const retryUpload = useCallback((sessionId) => {
    const session = sessions.find((s) => s.id === sessionId);
    const files = filesRef.current.get(sessionId);
    if (session && files) void runSession(sessionId, files, session.destinationLabel, session.module, session.moduleParam);
  }, [runSession, sessions]);
  const toggleBar = useCallback(() => setBarExpanded((prev) => !prev), []);

  const value = useMemo(
    () => ({
      sessions,
      barExpanded,
      enqueueUpload,
      minimizeBar,
      toggleBar,
      closeSessionNotification,
      hasActiveUploads: sessions.some(
        (s) =>
          s.status === "uploading" ||
          s.status === "queued" ||
          s.status === "failed"
      ),
    }),
    [sessions, barExpanded, enqueueUpload, minimizeBar, toggleBar, closeSessionNotification]
  );

  return (
    <DocumentUploadQueueContext.Provider value={value}>
      {children}
      <GlobalDocumentUploadBar
        sessions={sessions}
        barExpanded={barExpanded}
        onMinimize={minimizeBar}
        onToggleExpand={toggleBar}
        onCloseFailed={closeSessionNotification}
        onRetry={retryUpload}
      />
    </DocumentUploadQueueContext.Provider>
  );
}

function UploadFloatingToggle({ sessions, onExpand, t }) {
  const progress = aggregateProgress(sessions);
  const hasFailed = sessions.some((s) => s.status === "failed");
  const isUploading = sessions.some(
    (s) => s.status === "uploading" || s.status === "queued"
  );

  return (
    <button
      type="button"
      onClick={onExpand}
      className={TWclassNames(
        "fixed left-3 top-3 flex items-center gap-2 rounded-full shadow-lg transition-all pointer-events-auto sm:top-4",
        "border-2 px-3 py-2 min-h-[44px]",
        hasFailed
          ? "border-red-400 bg-red-50 text-red-800"
          : "border-blue-400 bg-blue-600 text-white hover:bg-blue-700"
      )}
      style={{ zIndex: DOCUMENT_UPLOAD_BAR_Z_INDEX + 1 }}
      aria-expanded={false}
      aria-label={t("documentUpload.bar.expand")}
      title={t("documentUpload.bar.expand")}
    >
      <span className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center">
        <svg className="absolute inset-0 h-9 w-9 -rotate-90" viewBox="0 0 36 36" aria-hidden>
          <circle
            cx="18"
            cy="18"
            r="15"
            fill="none"
            className={hasFailed ? "stroke-red-200" : "stroke-blue-400/40"}
            strokeWidth="3"
          />
          <circle
            cx="18"
            cy="18"
            r="15"
            fill="none"
            className={hasFailed ? "stroke-red-500" : "stroke-white"}
            strokeWidth="3"
            strokeDasharray={`${(progress / 100) * 94.2} 94.2`}
            strokeLinecap="round"
          />
        </svg>
        <ArrowUpTrayIcon
          className={`relative h-4 w-4 ${isUploading && !hasFailed ? "animate-pulse" : ""}`}
          aria-hidden
        />
      </span>
      <span className="flex flex-col items-start text-left leading-tight">
        <span className="text-xs font-semibold">
          {hasFailed
            ? t("documentUpload.bar.failed")
            : t("documentUpload.bar.uploadingShort")}
        </span>
        <span className="text-[11px] opacity-90">
          {progress}% · {sessions.length}{" "}
          {sessions.length === 1
            ? t("documentUpload.bar.jobSingular")
            : t("documentUpload.bar.jobPlural")}
        </span>
      </span>
      <ChevronDownIcon className="h-4 w-4 flex-shrink-0" aria-hidden />
    </button>
  );
}

function GlobalDocumentUploadBar({
  sessions,
  barExpanded,
  onMinimize,
  onToggleExpand,
  onCloseFailed,
  onRetry,
}) {
  const { t } = useTranslation();
  const visible = sessions.filter(
    (s) => s.status === "queued" || s.status === "uploading" || s.status === "failed"
  );

  if (visible.length === 0) {
    return null;
  }

  const ui = (
    <>
      {barExpanded && (
        <div
          className="fixed top-0 left-0 right-0 border-b border-blue-300 bg-blue-50 shadow-lg pointer-events-auto"
          style={{ zIndex: DOCUMENT_UPLOAD_BAR_Z_INDEX }}
          role="region"
          aria-live="polite"
          aria-label={t("documentUpload.bar.ariaLabel")}
        >
          <div className="mx-auto max-w-7xl px-4 py-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900">
                {t("documentUpload.bar.ariaLabel")}
              </p>
              <button
                type="button"
                onClick={onMinimize}
                className="rounded px-2 py-1 text-xs font-medium text-blue-800 hover:bg-blue-100"
              >
                {t("documentUpload.bar.minimize")}
              </button>
            </div>
            <div className="space-y-2">
            {visible.map((session) => {
              const currentFile =
                session.files?.[session.uploadedCount] || session.files?.[0];
              const fileLabel = currentFile?.title || currentFile?.name || "";

              return (
                <div
                  key={session.id}
                  className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-2">
                    {session.status === "failed" ? (
                      <XMarkIcon
                        className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600"
                        aria-hidden
                      />
                    ) : (
                      <ArrowUpTrayIcon
                        className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 animate-pulse"
                        aria-hidden
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      {session.previews?.length > 0 && <div className="flex gap-1">{(session.previewItems || session.previews.map((url) => ({ url, type: "image/" }))).map((entry) => entry.type?.startsWith("image/") ? <img key={entry.url} src={entry.url} alt="" className="h-10 w-10 rounded object-cover" /> : <span key={entry.url} title={entry.title} className="flex h-10 w-10 items-center justify-center rounded bg-slate-100 text-slate-500"><DocumentTextIcon className="h-6 w-6" aria-hidden="true" /></span>)}</div>}
                      <p className="text-sm font-semibold text-gray-900">
                        {session.status === "failed"
                          ? t("documentUpload.bar.failed")
                          : t("documentUpload.bar.uploading")}
                      </p>
                      <p className="truncate text-xs text-gray-600">
                        {t("documentUpload.bar.destination")}: {session.destinationLabel}
                      </p>
                      {fileLabel && session.status !== "failed" && (
                        <p className="truncate text-xs text-gray-500">
                          {fileLabel}{" "}
                          <span className="text-gray-400">
                            (
                            {t("documentUpload.bar.fileOf", {
                              current: Math.min(
                                session.uploadedCount + 1,
                                session.totalCount
                              ),
                              total: session.totalCount,
                            })}
                            )
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  {session.status !== "failed" && (
                    <div className="flex w-full flex-shrink-0 items-center gap-3 sm:w-64">
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-blue-100">
                        <div
                          className="h-full rounded-full bg-blue-600 transition-all duration-300"
                          style={{ width: `${session.overallProgress || 0}%` }}
                        />
                      </div>
                      <span className="w-10 text-right text-xs font-medium text-blue-800">
                        {session.overallProgress || 0}%
                      </span>
                    </div>
                  )}

                  {session.status === "failed" && (
                    <button type="button" onClick={() => onRetry(session.id)} className="rounded px-2 py-1 text-xs font-medium text-blue-700">{t("Retry")}</button>
                  )}
                  {session.status === "failed" && (
                    <button
                      type="button"
                      onClick={() => onCloseFailed(session.id)}
                      className="self-end rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 sm:self-center"
                    >
                      {t("documentUpload.bar.close")}
                    </button>
                  )}

                </div>
              );
            })}
            </div>
          </div>
        </div>
      )}

      {!barExpanded && (
        <UploadFloatingToggle
          sessions={visible}
          onExpand={onToggleExpand}
          t={t}
        />
      )}
    </>
  );

  return createPortal(ui, document.body);
}

export function useDocumentUploadQueue() {
  const ctx = useContext(DocumentUploadQueueContext);
  if (!ctx) {
    throw new Error("useDocumentUploadQueue must be used within DocumentUploadQueueProvider");
  }
  return ctx;
}

/** Safe hook when provider may be absent (tests). */
export function useDocumentUploadQueueOptional() {
  return useContext(DocumentUploadQueueContext);
}
